import React, { useCallback, useState } from 'react';
import {
	useIsNavigationInitialized,
	useIsNavigationLoading,
	useIsNavigationLocked,
	useNavigationViews,
} from '@jira/polaris__component-navigation-store/src/useIsNavigationLocked.tsx';
import { StartWithTemplateSpotlightWithModal } from '@jira/polaris__component-view-template-library/src/StartWithTemplateSpotlightWithModal.tsx';
import AddIcon from '@atlaskit/icon/core/add';

import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { useIsFullyExpanded } from '@jira/navigation-apps__sidebar-common/src/controllers/is-fully-expanded/index.tsx';
import { CommonNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/common-nav-menu-button/main.tsx';
import { CreateViewMenu, type TriggerProps } from '../create-view-menu/CreateViewMenu.tsx';
import {
	useAnalyticsL2,
	useContainerId,
	useJpdNavCanManageViews,
	useJpdNavViewName,
} from '../../controllers/context.tsx';
import { isNavigationProject, type NavigationContainer } from '../../common/types.tsx';

const createView = defineMessage({
	defaultMessage: 'Create a view',
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.action-button-create-view.create-view',
	description: 'Label for the create view button in the views sidebar navigation menu.',
});

type ActionButtonCreateViewProps = {
	navigationContainer: NavigationContainer;
	container?: string;
	sectionId?: string;
};

export const ActionButtonCreateViewNew = ({
	container,
	sectionId,
	navigationContainer,
}: ActionButtonCreateViewProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const navViewName = useJpdNavViewName();
	const containerId = useContainerId();
	const [isNavigationLocked] = useIsNavigationLocked({ containerId });
	const [isNavigationLoading] = useIsNavigationLoading({ containerId });
	const [isNavigationInitialized] = useIsNavigationInitialized({ containerId });
	const [navigationViews] = useNavigationViews({ containerId });
	const isSidebarFullyExpanded = useIsFullyExpanded();
	const canManageViews = useJpdNavCanManageViews();
	const isNavigationProjectContainer = isNavigationProject(navigationContainer);
	const isCreateButtonEnabled = canManageViews && !isNavigationLocked;
	const hasFetchedViews = navigationViews.length > 0;
	const isNavigationStable = isNavigationInitialized && !isNavigationLoading && hasFetchedViews;
	const [spotlightTargetNode, setSpotlightTargetNode] = useState<HTMLElement | null>(null);
	const isSpotlightDelayEligible = isNavigationStable && Boolean(spotlightTargetNode);
	const { sendCreateViewOnProjectMenuItemAnalyticsEvent } = useAnalyticsL2();

	const handleAnalyticsEvent = useCallback(() => {
		sendCreateViewOnProjectMenuItemAnalyticsEvent(navigationContainer);
	}, [navigationContainer, sendCreateViewOnProjectMenuItemAnalyticsEvent]);

	const renderTrigger = useCallback(
		({ onClick, ...rest }: TriggerProps) => {
			if (!isCreateButtonEnabled) {
				return null;
			}

			const renderIcon = (iconProps: React.ComponentProps<typeof AddIcon>): React.JSX.Element => (
				<AddIcon {...iconProps} size="small" />
			);
			const handleCreateButtonClick = (): void => {
				onClick();
				handleAnalyticsEvent();
			};

			const createButton = (
				<CommonNavMenuButton
					{...rest}
					ref={mergeRefs(rest.ref, setSpotlightTargetNode)}
					testId={`navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.action-button-create-view.button-${navViewName}`}
					icon={renderIcon}
					label={formatMessage(createView)}
					onClick={handleCreateButtonClick}
				/>
			);

			const createButtonWithTooltip = (
				<Tooltip content={formatMessage(createView)}>{createButton}</Tooltip>
			);

			if (
				isNavigationStable &&
				isNavigationProjectContainer &&
				spotlightTargetNode &&
				isSidebarFullyExpanded
			) {
				return (
					<StartWithTemplateSpotlightWithModal
						projectId={navigationContainer.projectId}
						projectKey={navigationContainer.projectKey}
						containerId={sectionId}
						spotlightTargetNode={spotlightTargetNode}
						isDelayEligible={isSpotlightDelayEligible}
					>
						{createButtonWithTooltip}
					</StartWithTemplateSpotlightWithModal>
				);
			}

			return createButtonWithTooltip;
		},
		[
			formatMessage,
			handleAnalyticsEvent,
			isCreateButtonEnabled,
			isNavigationProjectContainer,
			isNavigationStable,
			isSpotlightDelayEligible,
			isSidebarFullyExpanded,
			navViewName,
			navigationContainer,
			sectionId,
			spotlightTargetNode,
		],
	);

	return (
		<CreateViewMenu
			navigationContainer={navigationContainer}
			container={container}
			sectionId={sectionId}
			renderTrigger={renderTrigger}
		/>
	);
};
